import api from '@/services/api.service';

export interface OrderReviewChecklist {
  id: string;
  orc_number: string;
  review_date: string;
  opportunity_id: string;
  customer_name?: string | null;
  contact_person?: string | null;
  project_name?: string | null;
  rfq_reference?: string | null;
  inquiry_date?: string | null;
  due_date?: string | null;
  review_status: string;
  approval_status: string;
  priority?: string | null;
  remarks?: string | null;
  internal_notes?: string | null;
  overall_risk_rating?: string | null;
  final_recommendation?: string | null;
  approval_instance_id?: string | null;
  sections?: Record<string, unknown> | null;
  approved_at?: string | null;
  is_stale?: boolean;
  stale_reason?: string | null;
  sales_team_id?: string | null;
  sales_team_name?: string | null;
}

const base = '/crm/order-review-checklists';

function peel<T>(body: unknown): T {
  const outer = (body as { data?: unknown })?.data ?? body;
  const inner = (outer as { data?: unknown })?.data ?? outer;
  return inner as T;
}

class OrderReviewChecklistService {
  async list(params?: Record<string, string | undefined>): Promise<{ data: OrderReviewChecklist[]; total: number }> {
    const q = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== '') q.set(k, v);
    });
    const res = await api.get(`${base}${q.toString() ? `?${q.toString()}` : ''}`);
    const rows = peel<OrderReviewChecklist[]>(res);
    const meta = (res as any)?.meta || {};
    return { data: Array.isArray(rows) ? rows : [], total: Number(meta.total || (Array.isArray(rows) ? rows.length : 0)) };
  }

  async get(id: string): Promise<OrderReviewChecklist> {
    const res = await api.get(`${base}/${id}`);
    return peel<OrderReviewChecklist>(res);
  }

  async listByOpportunity(opportunityId: string): Promise<OrderReviewChecklist[]> {
    const res = await api.get(`${base}/by-opportunity/${opportunityId}`);
    const rows = peel<OrderReviewChecklist[]>(res);
    return Array.isArray(rows) ? rows : [];
  }

  async createFromOpportunity(opportunityId: string): Promise<OrderReviewChecklist> {
    const res = await api.post(`${base}/from-opportunity/${opportunityId}`, {});
    return peel<OrderReviewChecklist>(res);
  }

  async update(id: string, patch: Partial<OrderReviewChecklist>): Promise<OrderReviewChecklist> {
    const res = await api.put(`${base}/${id}`, patch);
    return peel<OrderReviewChecklist>(res);
  }

  async submit(id: string): Promise<OrderReviewChecklist> {
    const res = await api.post(`${base}/${id}/submit-for-approval`, {});
    return peel<OrderReviewChecklist>(res);
  }

  async approve(id: string, remarks?: string): Promise<OrderReviewChecklist> {
    const res = await api.post(`${base}/${id}/approve`, { remarks });
    return peel<OrderReviewChecklist>(res);
  }

  async rejectUniversal(id: string, payload: { rejection_reason_code: string; remarks?: string }): Promise<OrderReviewChecklist> {
    const res = await api.post(`${base}/${id}/reject-universal-approval`, payload);
    return peel<OrderReviewChecklist>(res);
  }

  async cancel(id: string): Promise<OrderReviewChecklist> {
    const res = await api.post(`${base}/${id}/cancel`, {});
    return peel<OrderReviewChecklist>(res);
  }
}

export default new OrderReviewChecklistService();

